import React from 'react';
import { ArrowRight, Ticket, Star, MessageSquare, User } from 'lucide-react';
import { formatBookingId } from '../../context/DashboardContext';

const RecentReservationsAndReviews = ({
  recentReservations = [],
  recentReviews = [],
  navigate,
  bookingPath = '/booking',
  reviewsPath = '/reviews'
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
      case 'completed':
        return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'Pending':
      case 'onTrip':
        return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'Cancelled':
        return 'bg-rose-50 text-rose-600 border-rose-100';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  const formatDateDisplay = (dateInput) => {
    if (!dateInput) return 'N/A';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return String(dateInput);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 pb-10">
      {/* Left Column: Recent Reservations */}
      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow duration-300 min-h-[380px] sm:min-h-[480px]">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="w-1.5 h-5 sm:h-6 rounded-full bg-[#067978]"></div>
            Recent Reservations
          </h3>
          <button
            onClick={() => navigate(bookingPath)}
            className="text-[#067978] hover:bg-teal-50 p-1.5 sm:p-2 rounded-xl transition-all group"
            title="View All Bookings"
          >
            <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        <div className="flex-1 space-y-2.5 sm:space-y-3 overflow-y-auto pr-1">
          {recentReservations.length > 0 ? (
            recentReservations.slice(0, 5).map((res, idx) => {
              const guestName =
                res.user_id?.name ||
                res.booking_id?.name ||
                res.guest_name ||
                res.name ||
                'Guest';
              const formattedId = formatBookingId(res.booking_id || res._id);
              const status = res.status || 'Pending';

              return (
                <div
                  key={res._id || idx}
                  onClick={() => navigate(bookingPath)}
                  className="flex items-center justify-between p-3 sm:p-4 bg-slate-50/70 hover:bg-white hover:shadow-sm rounded-xl sm:rounded-2xl border border-slate-100 transition-all cursor-pointer min-h-[64px]"
                >
                  <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-[#067978]/10 text-[#067978] flex items-center justify-center font-bold text-xs shrink-0">
                      <User size={15} />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-800 text-xs sm:text-sm truncate max-w-[120px] xs:max-w-[160px]">
                        {guestName}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-slate-400 font-mono lowercase">
                          id: {formattedId}
                        </span>
                        <span className="text-[10px] text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400 font-bold uppercase">
                          {formatDateDisplay(res.date || res.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <span className={`px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider border shrink-0 ${getStatusColor(status)}`}>
                    {status}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-slate-300 gap-2 border-2 border-dashed border-slate-100 rounded-2xl p-6">
              <Ticket size={28} className="opacity-30" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                No reservations found
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Right Column: Recent Reviews */}
      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100 flex flex-col hover:shadow-md transition-shadow duration-300 min-h-[380px] sm:min-h-[480px]">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="w-1.5 h-5 sm:h-6 rounded-full bg-amber-400"></div>
            Recent Reviews
          </h3>
          <button
            onClick={() => navigate(reviewsPath)}
            className="text-amber-500 hover:bg-amber-50 p-1.5 sm:p-2 rounded-xl transition-all group"
            title="View All Reviews"
          >
            <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        <div className="flex-1 space-y-2.5 sm:space-y-3 overflow-y-auto pr-1">
          {recentReviews.length > 0 ? (
            recentReviews.slice(0, 4).map((review, idx) => {
              const userName =
                review.user_name ||
                review.user_id?.name ||
                review.name ||
                'Guest User';
              const rating = review.rating || review.stars || 5;
              const comment =
                review.feedback_text ||
                review.comment ||
                review.review_text ||
                'Great experience!';

              return (
                <div
                  key={review._id || idx}
                  onClick={() => navigate(reviewsPath)}
                  className="bg-slate-50/70 hover:bg-white hover:shadow-sm p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-100 transition-all min-h-[84px] flex flex-col justify-center cursor-pointer"
                >
                  <div className="flex items-center gap-2.5 sm:gap-3 mb-1">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-amber-100/60 text-amber-600 flex items-center justify-center font-bold text-xs shrink-0 border border-amber-200/40">
                      {userName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-xs sm:text-sm text-slate-800 truncate">{userName}</h4>
                      <div className="flex text-amber-400 gap-0.5 mt-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={10}
                            fill={i < rating ? 'currentColor' : 'none'}
                            className={i < rating ? '' : 'text-slate-200'}
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400 font-bold shrink-0">
                      {formatDateDisplay(review.createdAt || review.date)}
                    </span>
                  </div>
                  <p className="text-[10px] sm:text-[11px] text-slate-600 font-medium leading-relaxed italic line-clamp-2 pl-1">
                    "{comment}"
                  </p>
                </div>
              );
            })
          ) : (
            <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-slate-300 gap-2 border-2 border-dashed border-slate-100 rounded-2xl p-6">
              <MessageSquare size={28} className="opacity-30" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                No reviews yet
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecentReservationsAndReviews;
